import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';
import { useWebsiteData } from '../context/WebsiteDataContext';
import { resolveImageUrl } from '../api';

const Portfolio = () => {
  const scrollContainerRef = useRef(null);
  const { portfolio } = useWebsiteData();

  const categoryColors = {
    'Strategy': { tagBg: 'bg-blue-500', lineColor: 'bg-blue-500' },
    'Branding': { tagBg: 'bg-purple-500', lineColor: 'bg-purple-500' },
    'SEO': { tagBg: 'bg-emerald-500', lineColor: 'bg-emerald-500' },
    'Email': { tagBg: 'bg-orange-500', lineColor: 'bg-orange-500' },
    'Creative': { tagBg: 'bg-rose-500', lineColor: 'bg-rose-500' },
    'Social': { tagBg: 'bg-indigo-500', lineColor: 'bg-indigo-500' },
  };

  const getColor = (category) => categoryColors[category] || { tagBg: 'bg-purple-500', lineColor: 'bg-purple-500' };

  // live API data only — placeholder rendered when an item has no uploaded image
  const projects = portfolio
    .slice(0, 6)
    .map((item, i) => ({
      id: item._id,
      number: String(i + 1).padStart(2, '0'),
      title: item.title,
      description: item.description,
      image: item.featuredImage ? resolveImageUrl(item.featuredImage) : null,
      category: item.category,
      ...getColor(item.category),
    }));

  // Auto-slide every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        
        // If at the end, scroll back to start
        if (container.scrollLeft >= maxScrollLeft - 10) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: 380, behavior: 'smooth' });
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -380, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 380, behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="portfolio"
      className="py-20 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #eff6ff 100%)'
      }}
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="absolute top-0 left-0 w-full opacity-20" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#dbeafe" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"/>
        </svg>
        <div className="absolute top-1/4 -left-32 w-[400px] h-[400px] bg-blue-100/50 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 -right-32 w-[300px] h-[300px] bg-purple-100/40 rounded-full blur-[80px]" />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-12">
          {/* Left Side - Title */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:w-[35%] flex-shrink-0"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-[3px] bg-blue-600 rounded-full"></span>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Our Portfolio</span>
            </div>

            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Latest Work Of
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                Successful Projects
              </span>
            </h2>

            <p className="text-slate-600 text-lg mb-10 leading-relaxed max-w-md">
              Explore our portfolio of successful digital marketing campaigns and transformative projects.
            </p>

            <div className="flex gap-4">
              <motion.button
                onClick={scrollLeft}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-14 h-14 bg-amber-400 rounded-full flex items-center justify-center text-white shadow-xl shadow-amber-400/30 cursor-pointer"
              >
                <FaArrowLeft className="text-lg" />
              </motion.button>
              <motion.button
                onClick={scrollRight}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-14 h-14 bg-slate-900 rounded-full flex items-center justify-center text-white shadow-xl shadow-slate-900/30 cursor-pointer"
              >
                <FaArrowRight className="text-lg" />
              </motion.button>
            </div>
          </motion.div>

          {/* Cards Container */}
          <div className="lg:flex-1 overflow-hidden">
            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex-shrink-0 w-[300px] sm:w-[320px] xl:w-[360px] group"
                >
                  <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-[1.5rem] overflow-hidden h-[380px] sm:h-[400px] xl:h-[440px] cursor-pointer shadow-2xl hover:shadow-slate-900/50 transition-all duration-300 hover:-translate-y-2">
                    <div className="absolute top-6 left-6 z-10">
                      <span className="text-6xl xl:text-7xl font-bold text-white/[0.07] group-hover:text-white/[0.12] transition-all duration-500 select-none">
                        {project.number}
                      </span>
                    </div>

                    <div className="absolute right-0 top-0 w-[70%] h-[50%] overflow-hidden rounded-bl-[2.5rem]">
                      {project.image ? (
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className={`w-full h-full ${project.tagBg} flex items-center justify-center`}>
                          <span className="text-white/40 text-6xl font-bold select-none">{project.title?.charAt(0)}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-800 via-slate-800/80 to-transparent" />
                    </div>

                    <button className="absolute top-[42%] right-6 w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center text-white shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:rotate-45 z-20">
                      <FaExternalLinkAlt className="text-sm" />
                    </button>

                    <div className="absolute bottom-0 left-0 right-0 p-6 xl:p-8 z-10">
                      <span className={`inline-block px-4 py-1.5 ${project.tagBg} text-white text-xs font-semibold rounded-full mb-4`}>
                        {project.category}
                      </span>
                      <h3 className="text-xl xl:text-2xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed mb-5 group-hover:text-slate-300 transition-colors">
                        {project.description}
                      </p>
                      <div className={`h-1 w-12 ${project.lineColor} rounded-full group-hover:w-20 transition-all duration-500`} />
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-slate-900 via-slate-900/90 to-transparent pointer-events-none" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
